import React, { useEffect, useMemo, useRef, useState, memo } from 'react';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import {
  FaPlus, FaCheckCircle, FaClock, FaUserAlt, FaLaptop,
  FaRegFileAlt, FaWhatsapp, FaTags, FaTimes, FaLayerGroup
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import axios from 'axios';

/* --------------------------------- APIs ---------------------------------- */
const API_INSTALLATION = 'https://dev.sliitleo.org/Backend/installation_reg.php';
const API_MEMBERS      = 'https://dev.sliitleo.org/Backend/member_api.php';

/* -------------------------------- Config --------------------------------- */
const SHOW_ONLY_VERIFIED = false;
const POLL_MS_VISIBLE = 5000;
const POLL_MS_HIDDEN  = 15000;
const TARGET = 400;

/* -------------------------------- Helpers -------------------------------- */
function pickInstallations(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload?.rows && Array.isArray(payload.rows)) return payload.rows;
  if (payload?.all && Array.isArray(payload.all)) return payload.all;
  if (payload?.data && Array.isArray(payload.data)) return payload.data;
  if (payload?.installations && Array.isArray(payload.installations)) return payload.installations;
  return [];
}
async function toJsonSafe(res) {
  const text = await res.text();
  try { return JSON.parse(text); } catch { throw new Error('Invalid JSON from server'); }
}
async function postForm(url, params) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    cache: 'no-store',
    body: params.toString(),
  });
  const raw = await res.text();
  let json; try { json = JSON.parse(raw); } catch { json = null; }
  return { ok: res.ok, status: res.status, raw, json };
}
function toWaPhone(v = '') {
  const digits = String(v).replace(/\D+/g, '');
  if (!digits) return '';
  if (digits.startsWith('94')) return digits;
  return '94' + digits.replace(/^0/, '');
}
function extractIdNameRole(m = {}) {
  const id = String(m.SLIITID ?? m.SLIITLeoID ?? m.SLIIT_Leo_ID ?? m.sliitid ?? m.sliitID ?? '').trim();
  const name = String(m.FullName ?? m.full_name ?? m.fullName ?? m.Fullname ?? m.Name ?? m.name ?? '').trim();
  const role = String(m.Role ?? m.role ?? m.userRole ?? m.UserRole ?? '').trim();
  return { id, name, role };
}
function isVerifiedMember(i){ return i.Verify === 1 || i.Verify === '1' || !!i.VerifiedDate; }
function getRegDate(i){ return i.RegDate || i.RegisteredDate || i.CreatedAt || i.CreatedDate || i.created_at || ''; }
function fmtDate(d){ try { return d ? new Date(d).toLocaleDateString() : ''; } catch { return ''; } }

/* ================================ PAGE =================================== */
export default function Installation() {
  const user = JSON.parse(localStorage.getItem('user')) || {
    SLIITID: '',
    name: 'Unknown User',
    role: 'Member',
    image: '/user.png'
  };

  const [verifyPopup, setVerifyPopup] = useState({ open: false, regid: null });
  const [reportModal, setReportModal] = useState({ open: false, regid: null, reason: '', loading: false, error: '' });

  const [search, setSearch] = useState('');
  const [segment, setSegment] = useState('all'); // all | verified | unverified | checked | notchecked
  const [sort, setSort] = useState('recent');   // recent | oldest | name

  const [rawInstallations, setRawInstallations] = useState([]);
  const [idToMeta, setIdToMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);

  const pollTimer = useRef(null);
  const currentPollAbort = useRef(null);

  /* ----------------------------- Boot & Poll ----------------------------- */
  const loadMemberDirectory = async () => {
    try {
      const res = await fetch(API_MEMBERS, { method: 'GET', cache: 'no-store' });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const data = await toJsonSafe(res);
      const arr = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
      const map = {};
      for (const m of arr) {
        const { id, name, role } = extractIdNameRole(m);
        if (id) map[id] = { name: name || '', role: role || '' };
      }
      setIdToMeta(map);
    } catch { setIdToMeta({}); }
  };

  const loadInstallations = async (signal) => {
    setErr('');
    try {
      const res = await fetch(API_INSTALLATION, { method: 'GET', cache: 'no-store', signal });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const data = await toJsonSafe(res);
      setRawInstallations(pickInstallations(data));
      setLastUpdated(new Date());
    } catch (e) {
      if (e.name === 'AbortError') return;
      setErr('Could not load registrations. Tap reload to try again.');
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadMemberDirectory(), loadInstallations()]);
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    function schedulePoll(ms) {
      clearInterval(pollTimer.current);
      pollTimer.current = setInterval(() => {
        if (currentPollAbort.current) currentPollAbort.current.abort();
        const ctrl = new AbortController();
        currentPollAbort.current = ctrl;
        loadInstallations(ctrl.signal);
      }, ms);
    }
    schedulePoll(POLL_MS_VISIBLE);
    const onVis = () => {
      schedulePoll(document.visibilityState === 'visible' ? POLL_MS_VISIBLE : POLL_MS_HIDDEN);
      if (document.visibilityState === 'visible') {
        if (currentPollAbort.current) currentPollAbort.current.abort();
        const ctrl = new AbortController();
        currentPollAbort.current = ctrl;
        loadInstallations(ctrl.signal);
      }
    };
    const onFocus = () => {
      if (currentPollAbort.current) currentPollAbort.current.abort();
      const ctrl = new AbortController();
      currentPollAbort.current = ctrl;
      loadInstallations(ctrl.signal);
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onFocus);
      clearInterval(pollTimer.current);
      if (currentPollAbort.current) currentPollAbort.current.abort();
    };
  }, []);

  /* --------------------------- Derivations ------------------------------- */
  const installations = useMemo(() => rawInstallations.map(r => ({
    ...r,
    VerifierName: r.VerifierName || r.VerifiedByName || (r.VerifiedBy ? (idToMeta[String(r.VerifiedBy)]?.name || '') : '') || '',
    CheckerName : r.CheckerName  || r.CheckedByName  || (r.CheckedBy  ? (idToMeta[String(r.CheckedBy )]?.name || '') : '') || ''
  })), [rawInstallations, idToMeta]);

  const currentUserRole = useMemo(() => {
    const meta = idToMeta[String(user.SLIITID)] || {};
    return (meta.role || user.role || '').trim();
  }, [idToMeta, user.SLIITID, user.role]);

  const isAdmin = useMemo(() => {
    const r = (currentUserRole || '').toLowerCase();
    return ['system admin', 'admin', 'administrator'].includes(r);
  }, [currentUserRole]);

  const totals = useMemo(() => {
    const total = installations.length;
    const checked = installations.filter(i => i.CheckIn === 1 || i.CheckIn === '1' || i.CheckIn === true).length;
    const verified = installations.filter(isVerifiedMember).length;
    const unverified = total - verified;
    const notchecked = total - checked;
    const food = installations.reduce((acc, item) => {
      const raw = String(item.FoodPref || '').trim().toLowerCase();
      if (/^veg(?:etarian)?$/.test(raw) || raw === 'vegan') acc.veg++;
      else if (/^non[\s-]?veg(?:etarian)?$/.test(raw) || raw === 'non') acc.non++;
      else if (raw) acc.other++; else acc.unset++;
      return acc;
    }, { veg:0, non:0, other:0, unset:0 });
    return { total, checked, verified, unverified, notchecked, food };
  }, [installations]);

  const pctToTarget = TARGET ? Math.min(100, Math.round((totals.total / TARGET) * 100)) : 0;

  const filtered = useMemo(() => {
    let list = [...installations];

    // search
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(i =>
        String(i.Name || '').toLowerCase().includes(q) ||
        String(i.SLIITID || '').toLowerCase().includes(q)
      );
    }
    // segment
    list = list.filter(i => {
      if (segment === 'verified')   return isVerifiedMember(i);
      if (segment === 'unverified') return !isVerifiedMember(i);
      if (segment === 'checked')    return i.CheckIn === 1 || i.CheckIn === '1' || i.CheckIn === true;
      if (segment === 'notchecked') return !(i.CheckIn === 1 || i.CheckIn === '1' || i.CheckIn === true);
      return true; // all
    });
    // sort
    list.sort((a,b) => {
      if (sort === 'name')   return String(a.Name||'').localeCompare(String(b.Name||''));
      const da = new Date(getRegDate(a) || a.created_at || 0).getTime();
      const db = new Date(getRegDate(b) || b.created_at || 0).getTime();
      return sort === 'oldest' ? (da - db) : (db - da);
    });
    return list;
  }, [installations, search, segment, sort]);

  /* ------------------------------ Actions -------------------------------- */
  const handleVerify = (regid) => { if (isAdmin) setVerifyPopup({ open:true, regid }); };
  const handleVerifyCancel = () => setVerifyPopup({ open:false, regid:null });

  const handleVerifyAccept = async () => {
    if (!isAdmin) return;
    const regid = verifyPopup.regid;
    setVerifyPopup({ open:false, regid:null });
    try {
      const params = new URLSearchParams();
      params.set('action','verify');
      params.set('RegID', String(regid));
      params.set('Verify','1');
      params.set('VerifiedBy', String(user.SLIITID || ''));
      params.set('VerifiedByName', String(user.name || 'Admin'));
      params.set('SendEmail','1');
      params.set('EmailType','verified');
      const { ok, json, status } = await postForm(API_INSTALLATION, params);
      if (!ok || !json?.success) throw new Error(json?.message || json?.error || `HTTP ${status}`);
      setRawInstallations(list => list.map(r => r.RegID === regid ? {
        ...r,
        Verify: 1,
        VerifiedBy: user.SLIITID,
        VerifierName: json.VerifierName || r.VerifierName || user.name || '',
        VerifiedDate: json.VerifiedDate || r.VerifiedDate || new Date().toISOString(),
      } : r));
    } catch (e) {
      alert(e.message || 'Failed to verify');
    }
  };

  const handleCheckIn = async (regid, currentStatus) => {
    if (!isAdmin) return;
    if (currentStatus === 1 || currentStatus === '1' || currentStatus === true) return;
    try {
      const params = new URLSearchParams();
      params.set('action','checkin');
      params.set('RegID', String(regid));
      params.set('CheckIn','1');
      params.set('CheckedBy', String(user.SLIITID || ''));
      params.set('CheckedByName', String(user.name || 'Admin'));
      const { ok, json, status } = await postForm(API_INSTALLATION, params);
      if (!ok || !json?.success) throw new Error(json?.message || json?.error || `HTTP ${status}`);
      setRawInstallations(list => list.map(r => r.RegID === regid ? {
        ...r,
        CheckIn: 1,
        CheckedBy: user.SLIITID,
        CheckerName: json.CheckerName || r.CheckerName || user.name || '',
        CheckedDate: json.CheckedDate || r.CheckedDate || new Date().toISOString(),
      } : r));
    } catch (e) {
      alert(e.message || 'Failed to check-in');
    }
  };

  const handleReportSubmit = async () => {
    if (!isAdmin) { setReportModal(prev => ({ ...prev, error: 'Only admins can report registrations.' })); return; }
    if (!reportModal.reason.trim()) { setReportModal(prev => ({ ...prev, error: 'Please enter a reason' })); return; }
    try {
      setReportModal(prev => ({ ...prev, loading:true, error:'' }));
      const formData = new FormData();
      formData.append('action','report');
      formData.append('RegID', reportModal.regid);
      formData.append('ReportReason', reportModal.reason);
      formData.append('ReportedBy', user.SLIITID || 'admin');
      formData.append('ReportedDate', new Date().toISOString().slice(0,19).replace('T',' '));
      const response = await axios.post(API_INSTALLATION, formData, { headers:{ 'Content-Type':'multipart/form-data' } });
      if (!response.data?.success) throw new Error(response.data?.error || 'Failed to submit');
      setRawInstallations(list => list.map(r => r.RegID === reportModal.regid ? { ...r, ReportReason: reportModal.reason } : r));
      setReportModal({ open:false, regid:null, reason:'', loading:false, error:'' });
    } catch (e) {
      setReportModal(prev => ({ ...prev, error: e.message || 'Failed', loading:false }));
    }
  };

  /* ------------------------------- UI ------------------------------------ */
  if (loading) {
    return (
      <div className="inst-root">
        <StyleScope />
        <div className="inst-bg" />
        <Sidebar />
        <Topbar user={user} />
        <main className="inst-layout">
          <Header total={totals.total} target={TARGET} pct={pctToTarget} lastUpdated={lastUpdated} />
          <Toolbar
            segment={segment} setSegment={setSegment}
            sort={sort} setSort={setSort}
            search={search} setSearch={setSearch}
            onReload={async ()=>{ setLoading(true); await Promise.all([loadInstallations(), loadMemberDirectory()]); setLoading(false); }}
            totals={totals}
          />
          <div className="inst-grid-4">{Array.from({ length: 4 }).map((_,i)=><div key={i} className="inst-skel-card" />)}</div>
          <div className="inst-skel-stack">{Array.from({ length: 8 }).map((_,i)=><div key={i} className="inst-skel-row" />)}</div>
        </main>
      </div>
    );
  }

  return (
    <div className="inst-root">
      <StyleScope />
      <div className="inst-bg" />

      {/* Verify modal (admins only) */}
      {verifyPopup.open && isAdmin && (
        <div className="inst-modal-overlay">
          <div className="inst-modal">
            <button onClick={handleVerifyCancel} className="inst-close" aria-label="Close"><FaTimes/></button>
            <div className="inst-circle success"><FaCheckCircle className="inst-icon" /></div>
            <div className="inst-modal-title">Verify Registration</div>
            <div className="inst-modal-sub">Confirm you want to <span className="inst-accent">verify</span> this registration.</div>
            <div className="inst-modal-actions">
              <button onClick={handleVerifyAccept} className="inst-btn inst-btn-success">Accept</button>
              <button onClick={handleVerifyCancel} className="inst-btn inst-btn-soft">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Report modal (admins only) */}
      {reportModal.open && isAdmin && (
        <div className="inst-modal-overlay">
          <div className="inst-modal md">
            <button onClick={()=>setReportModal(prev=>({ ...prev, open:false }))} className="inst-close" aria-label="Close"><FaTimes/></button>
            <h3 className="inst-modal-title">Report Issue</h3>
            <div className="inst-field">
              <label className="inst-label">Reason <span className="inst-danger">*</span></label>
              <textarea className="inst-textarea" rows={4} value={reportModal.reason} onChange={(e)=>setReportModal(prev=>({ ...prev, reason:e.target.value }))} placeholder="Add details..." />
              {reportModal.error && <p className="inst-hint-danger">{reportModal.error}</p>}
            </div>
            <div className="inst-modal-actions end">
              <button type="button" onClick={()=>setReportModal(prev=>({ ...prev, open:false }))} className="inst-btn inst-btn-soft" disabled={reportModal.loading}>Cancel</button>
              <button type="button" onClick={handleReportSubmit} className="inst-btn inst-btn-danger" disabled={reportModal.loading}>
                {reportModal.loading ? 'Submitting…' : 'Submit Report'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Sidebar />
      <Topbar user={user} />

      <main className="inst-layout">
        <Header total={totals.total} target={TARGET} pct={pctToTarget} lastUpdated={lastUpdated} />

        {/* CTA Row (Add Registration + Summary) */}
        <div className="inst-actions">
          <div className="spacer" />
          <Link to="/add-registration" className="cta-green" title="Add a new registration">
            <FaPlus /> <span>Add Registration</span>
          </Link>
          <Link to="/installation-report" className="cta-blue" title="Open summary dashboard">
            <FaLayerGroup /> <span>Summary</span>
          </Link>
        </div>

        {/* Toolbar: filters, search, sort, reload */}
        <Toolbar
          segment={segment} setSegment={setSegment}
          sort={sort} setSort={setSort}
          search={search} setSearch={setSearch}
          onReload={async ()=>{ setLoading(true); await Promise.all([loadInstallations(), loadMemberDirectory()]); setLoading(false); }}
          totals={totals}
        />

        {/* KPI deck – clear & compact */}
        <div className="inst-grid-4 inst-gap mb-3">
          <StatMini title="Verified" value={totals.verified} tone="good" />
          <StatMini title="Unverified" value={totals.unverified} tone="bad" />
          <StatMini title="Checked-in" value={totals.checked} tone="good" />
          <StatMini title="Not Checked-in" value={totals.notchecked} />
        </div>
        <div className="inst-grid-4 inst-gap mb-4">
          <StatMini title="Vegetarian" value={totals.food.veg} />
          <StatMini title="Non-Veg" value={totals.food.non} />
          <StatMini title="Other" value={totals.food.other} />
          <StatMini title="Unset" value={totals.food.unset} />
        </div>

        {/* List */}
        {filtered.length === 0 ? (
          <div className="inst-panel center">
            {err || 'No registrations found.'}
            {err && (
              <div style={{ marginTop: 12 }}>
                <button onClick={async ()=>{ setLoading(true); await Promise.all([loadInstallations(), loadMemberDirectory()]); setLoading(false); }} className="inst-btn inst-btn-primary xs">Reload</button>
              </div>
            )}
          </div>
        ) : (
          <div className="inst-list">
            {filtered.map(item => (
              <Tile
                key={item.RegID}
                item={item}
                isAdmin={isAdmin}
                onVerify={()=> handleVerify(item.RegID)}
                onReport={()=> setReportModal({ open:true, regid:item.RegID, reason:'', loading:false, error:'' })}
                onCheckIn={()=> handleCheckIn(item.RegID, item.CheckIn)}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

/* ============================ Small Components =========================== */

function Header({ total, target, pct, lastUpdated }) {
  return (
    <div className="inst-hero">
      <span className="inst-hero-left">
        <span className="inst-hero-icon"><FaLaptop className="text-white" /></span>
        <span className="inst-hero-title">Installation <span className="inst-hero-sub">/ Registration</span></span>
      </span>

      <div className="inst-progress">
        <div className="inst-progress-top">
          <span className="inst-progress-title">Total Registered</span>
          <span className="inst-progress-meta">{total} / {target}</span>
        </div>
        <div className="meter3" role="meter" aria-valuenow={pct} aria-valuemin="0" aria-valuemax="100">
          <div className="m3-track" />
          <div className="m3-fill" style={{ width: `${pct}%` }} />
          <div className="m3-glow" />
          <div className="m3-dot" style={{ left: `${pct}%` }} />
          <div className="m3-tick t25" /><div className="m3-tick t50" /><div className="m3-tick t75" />
        </div>
        <div className="inst-progress-foot">
          {pct}% of target {lastUpdated ? `· Updated ${lastUpdated.toLocaleTimeString()}` : ''}
        </div>
      </div>
    </div>
  );
}

function Toolbar({ segment, setSegment, sort, setSort, search, setSearch, onReload, totals }) {
  const pills = [
    { key:'all',        label:'All',            count: totals.total },
    { key:'verified',   label:'Verified',       count: totals.verified },
    { key:'unverified', label:'Unverified',     count: totals.unverified },
    { key:'checked',    label:'Checked-in',     count: totals.checked },
    { key:'notchecked', label:'Not Checked-in', count: totals.notchecked },
  ];
  return (
    <div className="toolbar">
      <div className="pills">
        {pills.map(p => (
          <button
            key={p.key}
            className={`pill ${segment === p.key ? 'active' : ''}`}
            onClick={()=> setSegment(p.key)}
            title={p.label}
          >
            <span className="dot" /> {p.label}
            <span className="count">{p.count}</span>
          </button>
        ))}
      </div>

      <div className="searchSort">
        <div className="inst-search">
          <span className="inst-search-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
              <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </span>
          <input
            type="text"
            placeholder="Search by name or SLIIT ID…"
            className="inst-search-input"
            value={search}
            onChange={(e)=>setSearch(e.target.value)}
          />
          <select className="inst-select" value={sort} onChange={(e)=>setSort(e.target.value)}>
            <option value="recent">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="name">Name A→Z</option>
          </select>
          <button onClick={onReload} className="inst-btn inst-btn-primary xs" title="Reload">Reload</button>
        </div>
      </div>
    </div>
  );
}

function StatMini({ title, value, tone }) {
  return (
    <div className={`inst-stat mini ${tone || ''}`}>
      <span className="inst-stat-mini-value">{value}</span>
      <span className="inst-stat-mini-title">{title}</span>
    </div>
  );
}

/* Memoized row for speed */
const Tile = memo(function Tile({ item, isAdmin, onVerify, onReport, onCheckIn }) {
  const wa = toWaPhone(item.Contact);
  const checked = item.CheckIn === 1 || item.CheckIn === '1' || item.CheckIn === true;
  const verified = isVerifiedMember(item);
  const isReported = !!(item.ReportReason && !/^(null|null|undefined|\s*)$/i.test(String(item.ReportReason)));
  const regDate = fmtDate(getRegDate(item));

  return (
    <div className="inst-card">
      {/* left: avatar */}
      <div className="inst-card-avatar">
        <div className="inst-avatar"><FaUserAlt/></div>
        <div className="inst-food">
          <div className="inst-food-label">
            {(() => {
              const fp = String(item.FoodPref || '').toLowerCase().trim();
              if (fp === 'veg' || fp === 'vegetarian') return 'Veg';
              if (fp === 'non-veg' || fp === 'nonveg' || fp === 'non vegetarian') return 'Non';
              return fp || 'N/A';
            })()}
          </div>
          <div className="inst-food-bar" />
        </div>
      </div>

      {/* middle: info */}
      <div className="inst-card-body">
        <div className="inst-row a">
          <span className="inst-name">{item.Name}</span>
          <span className="inst-id">{item.SLIITID}</span>
        </div>

        <div className="inst-sub">
          {item.Faculty}
          {regDate && <span style={{ marginLeft: 10, color:'#8aa0d8' }}>• Registered {regDate}</span>}
        </div>

        <div className="inst-tags">
          <span className="inst-tag text">
            {item.PrevStatus || (verified ? 'Verified' : 'Unverified')}
            {item.Amount && <span className="inst-amt">, {item.Amount}</span>}
          </span>
          {item.Bank && <span className="inst-tag chip"><FaClock className="inst-chip-mini" /> {item.Bank}</span>}
          {verified ? <span className="inst-badge success">Verified</span> : <span className="inst-badge danger">Unverified</span>}
          {checked && <span className="inst-badge green">Checked-in</span>}
          {isReported && <span className="inst-badge danger">Reported</span>}
        </div>

        <div className="inst-meta">
          {item.VerifierName && <span>Verified by <span className="inst-strong">{item.VerifierName}</span></span>}
          {item.VerifiedDate && <span className="inst-dim">on {fmtDate(item.VerifiedDate)}</span>}
          {checked && item.CheckerName && <span className="inst-green">• Checked by <span className="inst-strong">{item.CheckerName}</span></span>}
        </div>

        {/* Docs & contact — kept far from admin action to avoid misclicks */}
        <div className="inst-docs">
          {item.FileURL && (
            <button
              className="inst-btn inst-btn-ghost xs"
              onClick={()=>window.open(item.FileURL,'_blank')}
              title="View Payment Slip"
              type="button"
            >
              <FaRegFileAlt className="inst-file" /><span style={{ marginLeft:6 }}>View Slip</span>
            </button>
          )}
          {wa && (
            <a
              href={`https://wa.me/+${wa}`} target="_blank" rel="noopener noreferrer"
              className="inst-btn inst-btn-ghost xs" title="Chat on WhatsApp"
              style={{ marginLeft: item.FileURL ? 8 : 0 }}
            >
              <FaWhatsapp className="inst-wa" /><span style={{ marginLeft:6 }}>{item.Contact}</span>
            </a>
          )}
        </div>

        {/* Admin-only inline actions */}
        {isAdmin && !verified && !isReported && (
          <div className="inst-inline-actions">
            <button className="inst-btn inst-btn-primary xs" onClick={onVerify}>Verify</button>
            <button className="inst-btn inst-btn-danger xs ml" onClick={onReport}>Report</button>
          </div>
        )}

        {isReported && (
          <div className="inst-report">
            <span className="inst-strong">Reported Reason:</span> {item.ReportReason}
          </div>
        )}
      </div>

      {/* right: admin action column (hidden for non-admin) */}
      {isAdmin && (
        <div className="inst-card-actions">
          {verified ? (
            <button
              onClick={onCheckIn}
              disabled={checked}
              className={'inst-btn inst-btn-wide ' + (checked ? 'inst-btn-success' : 'inst-btn-primary')}
            >
              {checked ? 'Checked In' : 'Check-in'}
              {checked ? <FaCheckCircle className="inst-ml inst-ico-green" /> : <FaClock className="inst-ml" />}
            </button>
          ) : (
            <div className="inst-btn inst-btn-soft inst-btn-wide" style={{textAlign:'center', opacity:.95}}>Verify first</div>
          )}
        </div>
      )}
    </div>
  );
});

/* ============================ Forced / Scoped CSS ========================= */
function StyleScope() {
  return (
    <style>{`
:root{
  --sidebar-w: 256px; /* match your Sidebar */
  --topbar-h: 64px;   /* match your Topbar */
}

/* Base theme */
.inst-root{
  --bg:#050a15;
  --panel:#0e172b;
  --panel-2:#13243f;
  --panel-3:#0b1630;
  --border:rgba(255,255,255,.09);
  --text:#eef2ff;
  --sub:#9aa8c3;
  --accent:#6c8cff;
  --good:#10b981;
  --bad:#ef4444;
  --glow:0 14px 38px rgba(0,0,0,.35);
  --ring:0 0 0 1px rgba(108,140,255,.28), 0 10px 36px rgba(108,140,255,.18);
  color:var(--text);
  background:var(--bg);
  font-family: Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
}

/* iOS-26 vibe glass background */
.inst-bg{
  position:fixed; inset:0; z-index:-1;
  background:
    radial-gradient(900px 600px at 10% -10%, rgba(30,64,175,.35), transparent 60%),
    radial-gradient(900px 560px at 105% 0%, rgba(16,185,129,.28), transparent 55%),
    radial-gradient(1100px 720px at 50% 110%, rgba(99,102,241,.22), transparent 55%),
    linear-gradient(180deg, #040914, #060b16 60%, #070c17);
}
.inst-bg::after{
  content:""; position:absolute; inset:-1px; backdrop-filter: blur(6px);
  background: conic-gradient(from 180deg at 50% 50%, #ffffff09, #10b98110, #6c8cff10, #ffffff09);
  opacity:.5; mix-blend:soft-light; pointer-events:none;
}

/* Layout with fixed Sidebar/Topbar safety */
.inst-layout{
  min-height:100vh;
  padding: calc(var(--topbar-h) + 16px) 18px 84px;
}
@media (min-width: 1024px){
  .inst-layout{ padding-left: calc(var(--sidebar-w) + 24px); }
}

/* Hero */
.inst-hero{
  border:1px solid var(--border);
  border-radius:16px;
  background:linear-gradient(90deg, rgba(255,255,255,.06), rgba(255,255,255,.04));
  backdrop-filter: blur(10px);
  box-shadow:var(--glow);
  padding:10px 12px; margin-bottom:14px;
  display:flex; align-items:center; gap:10px;
}
.inst-hero-left{ display:flex; align-items:center; gap:10px; }
.inst-hero-icon{ display:grid; place-items:center; }
.inst-hero-title{ font-weight:900; letter-spacing:.2px; font-size:14px; }
.inst-hero-sub{ color:var(--sub); font-weight:700; }

/* Progress */
.inst-progress{ margin-left:auto; width:min(560px, 100%); }
.inst-progress-top{ display:flex; align-items:center; justify-content:space-between; margin-bottom:6px; }
.inst-progress-title{ font-weight:900; font-size:12.5px; }
.inst-progress-meta{ color:var(--sub); font-size:12px; }
.inst-progress-foot{ font-size:11px; color:var(--sub); margin-top:6px; text-align:right; }

.meter3{ position:relative; height:14px; border-radius:999px; overflow:hidden; }
.m3-track{
  position:absolute; inset:0;
  background:linear-gradient(180deg, #0b1a33, #0a1427), radial-gradient(24px 12px at 20% 50%, #22365c 40%, transparent 60%) repeat-x;
  border:1px solid #1c2b4a; opacity:.95;
}
.m3-fill{
  position:absolute; inset:0 auto 0 0;
  background:linear-gradient(90deg, #10b981, #34d399, #6ee7b7);
  border-right:1px solid #86efac;
  filter: drop-shadow(0 6px 18px rgba(16,185,129,.35));
  transition:width .5s cubic-bezier(.2,.8,.2,1);
}
.m3-glow{ position:absolute; inset:0; background:linear-gradient(180deg, transparent, rgba(255,255,255,.1), transparent); mix-blend-mode:screen; }
.m3-dot{ position:absolute; top:50%; width:12px; height:12px; border-radius:999px; transform:translate(-50%,-50%); background:#a7f3d0; box-shadow:0 0 0 3px rgba(16,185,129,.25); }
.m3-tick{ position:absolute; top:0; bottom:0; width:1px; background:#1f2f52; opacity:.7; }
.m3-tick.t25{ left:25%; } .m3-tick.t50{ left:50%; } .m3-tick.t75{ left:75%; }

/* CTA Row */
.inst-actions{ display:flex; align-items:center; gap:10px; margin-bottom:10px; }
.inst-actions .spacer{ flex:1; }
.cta-green, .cta-blue{
  display:inline-flex; align-items:center; gap:8px; padding:10px 14px; border-radius:12px; text-decoration:none;
  font-weight:900; border:1px solid transparent; box-shadow: 0 10px 28px rgba(0,0,0,.15);
  transition: transform .15s ease, filter .15s ease, box-shadow .15s ease;
}
.cta-green{ background: linear-gradient(135deg, #059669, #10b981, #34d399); color:#03140f; border-color: rgba(16,185,129,.45); }
.cta-blue { background: linear-gradient(135deg, #1f2b55, #2b3f7a, #3f5fb5); color:#eaf1ff; border-color: rgba(99,102,241,.35); }
.cta-green:hover, .cta-blue:hover{ transform: translateY(-1px); filter: brightness(1.05); }

/* Toolbar */
.toolbar{ display:flex; flex-wrap:wrap; gap:10px; align-items:center; margin-bottom:12px; }
.pills{ display:flex; flex-wrap:wrap; gap:8px; }
.pill{
  display:inline-flex; align-items:center; gap:8px; padding:7px 10px; border-radius:999px;
  background:rgba(255,255,255,.06); border:1px solid var(--border); color:#e8edf7; font-weight:900; font-size:12px;
}
.pill .dot{ width:8px;height:8px;border-radius:999px;background:linear-gradient(90deg,#6c8cff,#10b981); box-shadow:0 0 0 2px rgba(108,140,255,.18); }
.pill .count{ margin-left:4px; color:#cfd8f5; font-weight:800; }
.pill.active{ border-color: rgba(108,140,255,.35); box-shadow: var(--ring); }

.searchSort{ margin-left:auto; min-width:min(620px,100%); }
@media (max-width:900px){ .searchSort{ min-width:100%; } }

.inst-search{
  display:flex; align-items:center; gap:10px;
  background:rgba(255,255,255,.06); border:1px solid var(--border);
  border-radius:14px; padding:8px 10px; backdrop-filter: blur(8px);
}
.inst-search-icon{ color:var(--sub); }
.inst-search-input{ flex:1; background:transparent; border:none; outline:none; color:var(--text); font-size:14px; }
.inst-search-input::placeholder{ color:#6f7d98; }
.inst-select{
  appearance:none;
  background: #0f1c3f; color:#e6eaff; border:1px solid rgba(129,140,248,.28);
  padding:6px 10px; border-radius:10px; font-weight:800; font-size:12px;
}

/* KPI cards */
.inst-grid-4{ display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:12px; }
@media (min-width:768px){ .inst-grid-4{ grid-template-columns:repeat(4,minmax(0,1fr)); gap:14px; } }
.inst-gap{ gap:16px; }
.inst-stat{
  background:linear-gradient(180deg, var(--panel), var(--panel-2));
  border:1px solid var(--border); border-radius:14px; padding:12px; box-shadow:var(--glow);
}
.inst-stat.mini{ text-align:center; }
.inst-stat.mini .inst-stat-mini-value{ font-size:18px; font-weight:900; }
.inst-stat.mini .inst-stat-mini-title{ color:var(--sub); font-size:11px; margin-top:2px; }
.inst-stat.mini.good{ border-color: rgba(16,185,129,.28); }
.inst-stat.mini.bad { border-color: rgba(239,68,68,.28); }

/* Panels / skeletons */
.inst-panel{ background:var(--panel); border:1px solid var(--border); border-radius:16px; padding:20px; color:var(--sub); box-shadow:var(--glow); }
.inst-panel.center{text-align:center;}
.inst-skel-card{ height:58px; border-radius:14px; background:linear-gradient(90deg, rgba(255,255,255,.05), rgba(255,255,255,.03)); border:1px solid var(--border); animation:sk 1.2s infinite alternate; }
.inst-skel-row{ height:52px; border-radius:14px; background:linear-gradient(90deg, rgba(255,255,255,.05), rgba(255,255,255,.03)); border:1px solid var(--border); animation:sk 1.2s infinite alternate; }
.inst-skel-stack{ display:flex; flex-direction:column; gap:10px; }
@keyframes sk{ from{opacity:.55} to{opacity:.85} }

/* List */
.inst-list{ display:flex; flex-direction:column; gap:9px; }

/* Cards (compact, modern) */
.inst-card{
  display:flex; gap:10px; padding:9px 10px;
  background:linear-gradient(180deg, var(--panel), var(--panel-3));
  border:1px solid var(--border); border-radius:14px; box-shadow:var(--glow);
  transition: transform .15s ease, box-shadow .15s ease, border-color .15s ease;
  content-visibility:auto; contain-intrinsic-size: 120px 640px;
}
.inst-card:hover{ transform:translateY(-1px); border-color:rgba(108,140,255,.3); box-shadow:var(--ring); }
.inst-card-avatar{ display:flex; flex-direction:column; align-items:center; gap:4px; }
.inst-avatar{ width:40px;height:40px;border-radius:999px; display:grid; place-items:center; background:#1f2c4f; color:#fff; font-size:13px; box-shadow: inset 0 0 0 1px rgba(255,255,255,.06), 0 10px 28px rgba(20,34,64,.6); }
.inst-food{ display:flex; flex-direction:column; align-items:center; }
.inst-food-label{ color:var(--sub); font-size:9px; text-transform:uppercase; letter-spacing:.08em; }
.inst-food-bar{ width:24px;height:3px;border-radius:99px; background:linear-gradient(90deg,#22c55e,#60a5fa); margin-top:3px; }

.inst-card-body{ flex:1; min-width:0; display:flex; flex-direction:column; gap:4px; }
.inst-row.a{ display:flex; flex-wrap:wrap; align-items:center; gap:6px; }
.inst-name{ font-weight:900; font-size:14px; letter-spacing:.2px; }
.inst-id{ font-size:10.5px; color:#9fb1ff; background:#0f1c3f; border:1px solid rgba(129,140,248,.35); padding:2px 8px; border-radius:999px; font-weight:900; }
.inst-sub{ color:var(--sub); font-size:12px; overflow:hidden; white-space:nowrap; text-overflow:ellipsis; }

.inst-tags{ display:flex; flex-wrap:wrap; gap:6px; align-items:center; }
.inst-tag.text{ color:#d9e0f4; font-size:12px; }
.inst-amt{ color:#8cb1ff; font-weight:800; margin-left:4px; }
.inst-tag.chip{ display:inline-flex; align-items:center; gap:6px; background:#0f1c3f; color:#e6eaff; border:1px solid rgba(129,140,248,.28); padding:3px 8px; border-radius:999px; font-size:11px; }
.inst-chip-mini{ font-size:12px; }

.inst-badge{ display:inline-flex; align-items:center; padding:3px 8px; border-radius:999px; font-size:11px; font-weight:900; border:1px solid rgba(255,255,255,.1); }
.inst-badge.success{ background:rgba(108,140,255,.1); color:#c6d5ff; border-color:rgba(108,140,255,.3); }
.inst-badge.green{ background:rgba(16,185,129,.12); color:#b5f7c7; border-color:rgba(16,185,129,.35); }
.inst-badge.danger{ background:rgba(239,68,68,.12); color:#ffc0c0; border-color:rgba(239,68,68,.35); }

.inst-meta{ color:var(--sub); font-size:12px; display:flex; flex-wrap:wrap; gap:8px; align-items:center; }
.inst-strong{ color:#d9e7ff; font-weight:900; }
.inst-dim{ color:#8b94aa; }
.inst-green{ color:#7af0a8; }
.inst-danger{ color:var(--bad); font-weight:900; }

.inst-docs{ display:flex; gap:8px; margin-top:6px; }

/* Admin action column (hidden for non-admin) */
.inst-card-actions{ display:flex; flex-direction:column; gap:8px; align-items:flex-end; min-width:150px; }
@media (max-width: 640px){ .inst-card-actions{ min-width:unset; align-items:stretch; } }

/* Buttons */
.inst-btn{
  border:1px solid var(--border); border-radius:12px; padding:7px 10px;
  color:#fff; background:var(--panel-2); font-weight:900; font-size:12px; box-shadow:var(--glow);
  transition: transform .12s ease, box-shadow .12s ease, border-color .12s ease, background .12s ease;
  cursor:pointer;
}
.inst-btn:hover{ transform:translateY(-1px); box-shadow:var(--ring); border-color:rgba(108,140,255,.35); }
.inst-btn:disabled{ opacity:.65; cursor:not-allowed; transform:none; box-shadow:var(--glow); }
.inst-btn.xs{ padding:6px 10px; border-radius:10px; font-size:12px; }
.inst-btn-wide{ width:100%; display:flex; justify-content:center; align-items:center; gap:6px; }
.inst-btn-primary{ background:linear-gradient(135deg,#2563eb,#7c3aed); border-color:rgba(124,58,237,.45); }
.inst-btn-success{ background:linear-gradient(135deg,#16a34a,#22c55e); border-color:rgba(34,197,94,.45); }
.inst-btn-ghost  { background:#0f1730; }
.inst-btn-danger { background:linear-gradient(135deg,#dc2626,#ef4444); border-color:rgba(239,68,68,.45); }
.inst-btn-soft   { background:rgba(255,255,255,.06); }
.inst-wa{ color:#40d26b; } .inst-file{ color:#8cb1ff; } .inst-ml{ margin-left:6px; } .inst-ico-green{ color:#b5f7c7; }

/* Modal */
.inst-modal-overlay{ position:fixed; inset:0; background:rgba(0,0,0,.6); display:grid; place-items:center; z-index:60; padding:16px; }
.inst-modal{ position:relative; width:100%; max-width:460px; background:linear-gradient(180deg,var(--panel), var(--panel-3)); border:1px solid var(--border); border-radius:18px; padding:20px; box-shadow:var(--ring); }
.inst-modal.md{ max-width:560px; }
.inst-close{ position:absolute; top:10px; right:12px; color:#a1acc0; background:rgba(255,255,255,.06); border:1px solid var(--border); border-radius:10px; width:32px;height:32px; display:grid; place-items:center; cursor:pointer; }
.inst-close:hover{ color:white; border-color:rgba(108,140,255,.35); }
.inst-circle{ width:56px;height:56px; border-radius:999px; display:grid; place-items:center; margin:0 auto 10px auto; }
.inst-circle.success{ background:rgba(34,197,94,.1); color:#22c55e; }
.inst-icon{ width:28px;height:28px; }
.inst-modal-title{ text-align:center; font-weight:900; font-size:18px; margin-bottom:6px; }
.inst-modal-sub{ text-align:center; color:var(--sub); font-size:14px; margin-bottom:12px; }
.inst-accent{ color:#9ab6ff; font-weight:900; }
.inst-modal-actions{ display:flex; gap:10px; justify-content:center; }
.inst-modal-actions.end{ justify-content:flex-end; }
.inst-field{ margin:12px 0; }
.inst-label{ display:block; color:#dfe6f5; font-size:13px; margin-bottom:6px; }
.inst-textarea{ width:100%; background:#0f1730; color:#e8edf7; border:1px solid var(--border); border-radius:12px; padding:10px; resize:vertical; min-height:110px; }
.inst-hint-danger{ color:#ffb4b4; font-size:12px; margin-top:6px; }

/* Reduced motion */
@media (prefers-reduced-motion: reduce) {
  .cta-green, .cta-blue, .inst-card, .inst-btn, .m3-fill { transition: none !important; }
}
`}</style>
  );
}
